import { cn } from '@/shared/utils/cn';
import type {
  CatalogCardInteractionContext,
  CatalogCardPersonFamilyPropsFlat,
  CatalogSupplierContactRow,
} from '../../types';
import { forwardRef, useMemo, useRef, type KeyboardEvent, type MouseEvent } from 'react';
import type { PopupRef } from '@/shared/types/popup';
import { Icon } from '@/modules/Icon';
import { Button } from '@/modules/Button';
import { Popup } from '@/modules/Popup';
import { Avatar } from '@/modules/Avatar';
import { Badge } from '@/modules/Badge';
import { faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import { faEnvelope, faPhone, faStar } from '@fortawesome/pro-solid-svg-icons';
import { OptionsMenuPanel } from './catalogCardMenuPanels';
import { stopCardClick } from './catalogCardMenuPanelUtils';
import { Tooltip, TruncatedWithTooltip } from '@/modules/Tooltip';
import { getCatalogCardRatingDisplayText } from '../../utils/catalogCardRatingDisplay';

function normalizeContact(
  row: CatalogSupplierContactRow | string,
  defaultLabel: string,
): CatalogSupplierContactRow {
  return typeof row === 'string' ? { label: defaultLabel, value: row } : row;
}

/** Tooltips fijos (no expuestos en la API) alineados con la variante catálogo (`Popup`/`Button`/`Tooltip`). */
const supplierOptionsTooltipConfig = { label: 'Más opciones', position: 'top' as const };
const supplierRatingTooltipLabel = 'Evaluación';
const supplierStatusTooltipLabel = 'Estatus';

/**
 * Variante proveedor o usuario (270px): cabecera con avatar, contacto y CTA cerrados por props.
 * Familia `user`: sin bloque de rating; foto opcional en el avatar (`Avatar` `people` vs iniciales).
 * Tooltips fijos en ⋯, bloque de calificación (solo proveedor) y badge de estado.
 * Con `disabled`: sin hover de tarjeta ni enlace principal; CTA desactivado; `Avatar` y badges/iconos de contacto en estilo
 * deshabilitado; el menú **⋯** sigue activo (como en catálogo).
 */
export const CatalogSupplierCard = forwardRef<HTMLDivElement, CatalogCardPersonFamilyPropsFlat>(
  function CatalogSupplierCard(props, ref) {
    const family = props.family;
    const ratingValue = family === 'supplier' ? props.ratingValue : undefined;
    const avatarImageSrc = family === 'user' ? props.avatarImageSrc : undefined;
    const avatarImageAlt = family === 'user' ? props.avatarImageAlt : undefined;
    const hideRating = family === 'user';

    const {
      className,
      selected = false,
      emphasized = false,
      subtleBorder = false,
      to,
      href,
      cardLinkTarget = '_self',
      onClick,
      statusLabel,
      avatarInitials,
      name,
      subtitle,
      phone,
      email,
      primaryButtonLabel = 'Ver detalles',
      onPrimaryClick,
      onOptionsClick,
      optionsMenuItems,
      disabled = false,
    } = props;
    const optionsMenuPopupRef = useRef<PopupRef>(null);
    const phoneRow = phone !== undefined ? normalizeContact(phone, 'Teléfono') : null;
    const emailRow = email !== undefined ? normalizeContact(email, 'Email') : null;
    const hasOptionsMenu = Boolean(optionsMenuItems?.length);
    const statusText = statusLabel?.trim() ?? '';
    const showStatusBadge = statusText.length > 0;
    const trimmedRating = (ratingValue ?? '').trim();
    const ratingDisplayText = getCatalogCardRatingDisplayText(trimmedRating);
    /** Hay calificación explícita. Sin calificación: estrella `disabled` y texto fijo `0.0` (no configurable). */
    const hasRatedValue = trimmedRating.length > 0;
    const showHeaderMetaRow = hideRating ? showStatusBadge : hasRatedValue || showStatusBadge;
    const widthClass = 'ui-w-[270px] ui-min-w-[270px] ui-max-w-[270px]';
    const trimmedAvatarSrc = (avatarImageSrc ?? '').trim();
    const showAvatarImage = trimmedAvatarSrc.length > 0;
    const resolvedAvatarAlt = (avatarImageAlt ?? '').trim() || name;

    const cardContext: CatalogCardInteractionContext = useMemo(
      () => ({ family, title: name }),
      [family, name],
    );

    const handleRootClick = (e: MouseEvent<HTMLDivElement>) => {
      if (!onClick) return;
      e.stopPropagation();
      onClick(cardContext);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (!onClick) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(cardContext);
      }
    };

    const cardDestination = (to ?? href ?? '').trim();
    const isCardLink = cardDestination.length > 0;
    /** Con `disabled`, no hay `<a>` ni navegación desde la zona principal. */
    const effectiveCardLink = Boolean(isCardLink && !disabled);
    /** Clic/teclado en la raíz cuando hay `onClick` y no hay enlace efectivo (incluye tarjeta bloqueada). */
    const rootCardClickActive = Boolean(onClick && !effectiveCardLink);

    const supplierLinkMainClass =
      'ui-flex ui-min-w-0 ui-w-full ui-flex-col ui-gap-16 ui-text-inherit ui-no-underline focus-visible:ui-outline focus-visible:ui-outline-2 focus-visible:ui-outline-offset-2 focus-visible:ui-outline-brand';

    const optionsTrigger = hasOptionsMenu ? (
      <Popup
        ref={optionsMenuPopupRef}
        position="top-left"
        color="secondary"
        size="xs"
        pill="pill"
        rounded
        popoverArrow={false}
        theme="solid"
        icon={faEllipsis}
        tooltip={supplierOptionsTooltipConfig}
      >
        <OptionsMenuPanel
          items={optionsMenuItems!}
          onDismiss={() => optionsMenuPopupRef.current?.close()}
        />
      </Popup>
    ) : (
      <Button
        color="secondary"
        size="xs"
        pill="pill"
        rounded
        theme="solid"
        icon={faEllipsis}
        tooltip={supplierOptionsTooltipConfig}
        aria-label="Opciones"
        onClick={(e) => {
          stopCardClick(e);
          onOptionsClick?.(cardContext);
        }}
      />
    );

    const rootClassName = cn(
      'ui-imj',
      !disabled && 'ui-group',
      'ui-relative ui-flex ui-flex-col ui-gap-16 ui-rounded-surface-card ui-border-1 ui-bg-fill ui-pb-16 ui-shadow-01',
      subtleBorder ? 'ui-border-subtle' : 'ui-border-default',
      widthClass,
      emphasized ? 'ui-bg-surface-brand-hover ui-shadow-03' : '',
      !disabled &&
        'ui-transition-all ui-duration-200 hover:ui-bg-surface-brand-hover hover:ui-shadow-03',
      ((!disabled && isCardLink) || onClick) && 'ui-cursor-pointer',
      disabled && !onClick && 'ui-cursor-default',
      className,
    );

    const supplierMainBlocks = (
      <>
        <header
          className={cn(
            'ui-flex ui-w-full ui-flex-col ui-gap-8 ui-rounded-t-surface-card ui-border-b ui-border-subtle ui-bg-fill ui-p-16',
            'ui-transition-colors ui-duration-200',
            !disabled && 'group-hover:ui-bg-surface-hover',
          )}
        >
          {showHeaderMetaRow ? (
            <div
              className={cn(
                'ui-flex ui-w-full ui-items-center',
                hideRating && showStatusBadge
                  ? 'ui-justify-end'
                  : showStatusBadge && showHeaderMetaRow
                    ? 'ui-justify-between'
                    : 'ui-justify-start',
              )}
            >
              {!hideRating && showHeaderMetaRow ? (
                <Tooltip label={supplierRatingTooltipLabel} position="top" preserveLayout>
                  <div className="ui-flex ui-min-w-0 ui-items-center ui-gap-4">
                    <Icon name={faStar} size="xs" color={hasRatedValue ? 'yellow' : 'disabled'} />
                    <TruncatedWithTooltip
                      title={ratingDisplayText}
                      position="top"
                      maxVisibleChars={5}
                      className="ui-text-[10px] ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-[2px] ui-text-tertiary"
                    >
                      {ratingDisplayText}
                    </TruncatedWithTooltip>
                  </div>
                </Tooltip>
              ) : null}
              {showStatusBadge ? (
                <Tooltip label={supplierStatusTooltipLabel} position="top" preserveLayout>
                  <span className="ui-inline-flex">
                    <Badge
                      label={statusText}
                      color={disabled ? 'gray' : 'success'}
                      theme="soft"
                      size="md"
                      stroke={!disabled}
                    />
                  </span>
                </Tooltip>
              ) : null}
            </div>
          ) : null}
          <div className="ui-flex ui-w-full ui-flex-col ui-items-center ui-gap-12">
            {showAvatarImage ? (
              <Avatar
                type="people"
                src={trimmedAvatarSrc}
                alt={resolvedAvatarAlt}
                size="md"
                disabled={disabled}
              />
            ) : (
              <Avatar
                type="letter"
                text={avatarInitials}
                theme="solid"
                color="blue"
                size="md"
                disabled={disabled}
              />
            )}
            <div className="ui-flex ui-w-full ui-flex-col ui-items-center ui-text-center">
              <p className="ui-w-full ui-truncate ui-text-body-sm-regular ui-font-body-sm-regular ui-leading-body-sm-regular ui-tracking-body-sm ui-text-primary">
                {name}
              </p>
              <p className="ui-w-full ui-truncate ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs ui-text-secondary">
                {subtitle}
              </p>
            </div>
          </div>
        </header>

        {phoneRow || emailRow ? (
          <div className="ui-flex ui-flex-col ui-gap-12 ui-px-16">
            {phoneRow ? (
              <div className="ui-flex ui-w-full ui-items-center ui-gap-4">
                <span className="ui-flex ui-size-24 ui-shrink-0 ui-items-center ui-justify-center ui-rounded-control-pill ui-bg-surface-brand-default">
                  <Icon name={faPhone} size="xs" color={disabled ? 'disabled' : 'brand'} />
                </span>
                <div className="ui-flex ui-min-w-0 ui-flex-1 ui-flex-col ui-gap-0">
                  <p className="ui-text-body-xs-medium ui-font-body-xs-medium ui-leading-body-xs-medium ui-tracking-body-xs ui-text-primary">
                    {phoneRow.label}
                  </p>
                  <p className="ui-truncate ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs ui-text-secondary">
                    {phoneRow.value}
                  </p>
                </div>
              </div>
            ) : null}
            {emailRow ? (
              <div className="ui-flex ui-w-full ui-items-center ui-gap-4">
                <span className="ui-flex ui-size-24 ui-shrink-0 ui-items-center ui-justify-center ui-rounded-control-pill ui-bg-surface-brand-default">
                  <Icon name={faEnvelope} size="xs" color={disabled ? 'disabled' : 'brand'} />
                </span>
                <div className="ui-flex ui-min-w-0 ui-flex-1 ui-flex-col ui-gap-0">
                  <p className="ui-text-body-xs-medium ui-font-body-xs-medium ui-leading-body-xs-medium ui-tracking-body-xs ui-text-primary">
                    {emailRow.label}
                  </p>
                  <p className="ui-truncate ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs ui-text-secondary">
                    {emailRow.value}
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </>
    );

    const supplierFooter = (
      <div className="ui-flex ui-items-center ui-gap-10 ui-px-16">
        {optionsTrigger}
        <Button
          color="primary"
          theme="solid"
          size="sm"
          fullWidth
          onClick={(e) => {
            stopCardClick(e);
            onPrimaryClick?.(cardContext);
          }}
        >
          {primaryButtonLabel}
        </Button>
      </div>
    );

    return (
      <div
        ref={ref}
        role={rootCardClickActive ? 'button' : undefined}
        tabIndex={rootCardClickActive ? 0 : undefined}
        aria-disabled={disabled && !onClick ? true : undefined}
        data-selected={selected ? true : undefined}
        data-disabled={disabled ? true : undefined}
        className={rootClassName}
        onClick={rootCardClickActive ? handleRootClick : undefined}
        onKeyDown={rootCardClickActive ? handleKeyDown : undefined}
      >
        {effectiveCardLink ? (
          <a
            href={cardDestination}
            target={cardLinkTarget}
            rel={cardLinkTarget === '_blank' ? 'noopener noreferrer' : undefined}
            className={supplierLinkMainClass}
            aria-label={name}
            onClick={() => {
              onClick?.(cardContext);
            }}
          >
            {supplierMainBlocks}
          </a>
        ) : (
          supplierMainBlocks
        )}
        {supplierFooter}
      </div>
    );
  },
);

CatalogSupplierCard.displayName = 'CatalogSupplierCard';
